import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';

import { db } from '@/firebase-config';
import { normalizeNoteType } from '@/features/notes/note-type';
import type { NotesReportType } from '@/features/notes/rehearsal-report-types';
import type { Note, NotePublishedReportMetadata } from '@/features/notes/types';
import { buildProjectSubcollectionPath, type ProjectScope } from '@/features/sync/project-scope';
import { sanitizeDocumentForFirestore } from '@/utils/firestore-sanitizer';
import { logger } from '@/utils/logger';

import {
  serializeSnapshotPreview,
  type CreateNoteEditorSnapshotInput,
  type NoteEditorSnapshot,
  type RawNotesClient,
} from '../contracts/raw-notes-client';

type FirestoreDocumentSnapshot = {
  id: string;
  exists?: () => boolean;
  data: () => unknown;
};

type FirestoreCollectionSnapshot = {
  docs: FirestoreDocumentSnapshot[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

type TimestampLike = {
  toDate?: () => Date;
  toMillis?: () => number;
  seconds?: number;
  nanoseconds?: number;
};

function isTimestampLike(value: unknown): value is TimestampLike {
  return isRecord(value) && (
    typeof value.toDate === 'function' ||
    typeof value.toMillis === 'function' ||
    typeof value.seconds === 'number'
  );
}

function isoFromDate(date: Date, fallback: string): string {
  return Number.isFinite(date.getTime()) ? date.toISOString() : fallback;
}

function dateFromTimestampLike(value: TimestampLike): Date | null {
  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date ? date : null;
  }
  if (typeof value.toMillis === 'function') {
    return new Date(value.toMillis());
  }
  if (typeof value.seconds !== 'number') {
    return null;
  }

  const nanoseconds = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
  return new Date((value.seconds * 1000) + Math.floor(nanoseconds / 1_000_000));
}

function isoTimestampValue(value: unknown, fallback: string): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Date) {
    return isoFromDate(value, fallback);
  }
  if (!isTimestampLike(value)) {
    return fallback;
  }

  const date = dateFromTimestampLike(value);
  return date ? isoFromDate(date, fallback) : fallback;
}

function searchIndexValue(value: unknown): Note['searchIndex'] {
  return Array.isArray(value) ? value as Note['searchIndex'] : [];
}

function isNotesReportType(value: unknown): value is NotesReportType {
  return value === 'rehearsal' || value === 'meeting';
}

function publishedReportMetadataValue(value: unknown): NotePublishedReportMetadata | null {
  if (!isRecord(value) || !isNotesReportType(value.type)) {
    return null;
  }
  if (
    typeof value.reportId !== 'string' ||
    typeof value.canonicalUrl !== 'string' ||
    typeof value.postedAt !== 'string'
  ) {
    return null;
  }

  const metadata: NotePublishedReportMetadata = {
    reportId: value.reportId,
    canonicalUrl: value.canonicalUrl,
    postedAt: value.postedAt,
    type: value.type,
  };
  if (typeof value.artifactId === 'string') {
    metadata.artifactId = value.artifactId;
  }
  return metadata;
}

function publishedReportsValue(value: unknown): Note['publishedReports'] {
  if (!isRecord(value)) {
    return;
  }

  const reports: Note['publishedReports'] = {};
  for (const reportType of ['rehearsal', 'meeting'] satisfies NotesReportType[]) {
    const metadata = publishedReportMetadataValue(value[reportType]);
    if (metadata?.type === reportType) {
      reports[reportType] = metadata;
    }
  }

  return Object.keys(reports).length > 0 ? reports : undefined;
}

function publishedReportsByReportIdValue(value: unknown): Note['publishedReportsByReportId'] {
  if (!isRecord(value)) {
    return;
  }

  const reports: Note['publishedReportsByReportId'] = {};
  for (const [reportId, candidate] of Object.entries(value)) {
    const metadata = publishedReportMetadataValue(candidate);
    if (metadata?.reportId === reportId) {
      reports[reportId] = metadata;
    }
  }

  return Object.keys(reports).length > 0 ? reports : undefined;
}

function notesCollection(scope: ProjectScope) {
  return collection(db, buildProjectSubcollectionPath(scope, 'notes'));
}

function snapshotsCollection(scope: ProjectScope, noteId: string) {
  return collection(db, `${buildProjectSubcollectionPath(scope, 'notes')}/${noteId}/editorSnapshots`);
}

function snapshotDoc(scope: ProjectScope, noteId: string, snapshotId: string) {
  return doc(db, `${buildProjectSubcollectionPath(scope, 'notes')}/${noteId}/editorSnapshots/${snapshotId}`);
}

function hasNoteMetadata(record: Record<string, unknown>, id: string): boolean {
  return record.id === id && (
    typeof record.createdAt === 'string' ||
    record.createdAt instanceof Date ||
    isTimestampLike(record.createdAt)
  );
}

function noteFromSnapshot(docSnap: FirestoreDocumentSnapshot): Note | null {
  const data = docSnap.data();
  if (!isRecord(data) || !hasNoteMetadata(data, docSnap.id)) {
    return null;
  }

  const note: Note = {
    id: docSnap.id,
    title: stringValue(data.title, 'Untitled Note'),
    type: normalizeNoteType(data.type),
    content: stringValue(data.content, ''),
    createdAt: isoTimestampValue(data.createdAt, ''),
    updatedAt: isoTimestampValue(data.updatedAt, ''),
    searchIndex: searchIndexValue(data.searchIndex),
  };
  const publishedReports = publishedReportsValue(data.publishedReports);
  const publishedReportsByReportId = publishedReportsByReportIdValue(data.publishedReportsByReportId);
  return {
    ...note,
    ...(publishedReports ? { publishedReports } : {}),
    ...(publishedReportsByReportId ? { publishedReportsByReportId } : {}),
  };
}

function notesFromSnapshot(snapshot: FirestoreCollectionSnapshot): Note[] {
  return snapshot.docs.flatMap(docSnap => {
    const note = noteFromSnapshot(docSnap);
    return note ? [note] : [];
  });
}

function editorSnapshotFromDoc(docSnap: FirestoreDocumentSnapshot): NoteEditorSnapshot {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...(isRecord(data) ? data : {}),
  } as NoteEditorSnapshot;
}

function editorSnapshotsFromSnapshot(snapshot: FirestoreCollectionSnapshot): NoteEditorSnapshot[] {
  return snapshot.docs.map(editorSnapshotFromDoc);
}

export class FirebaseRawNotesClient implements RawNotesClient {
  async listNotes(scope: ProjectScope): Promise<Note[]> {
    const snapshot = await getDocs(notesCollection(scope));
    return notesFromSnapshot(snapshot as FirestoreCollectionSnapshot);
  }

  subscribeNotes(scope: ProjectScope, listener: (notes: Note[]) => void) {
    return onSnapshot(
      notesCollection(scope),
      snapshot => {
        listener(notesFromSnapshot(snapshot as FirestoreCollectionSnapshot));
      },
      error => {
        logger.error('[FirebaseRawNotesClient] Notes subscription error:', error);
      },
    );
  }

  async createEditorSnapshot(input: CreateNoteEditorSnapshotInput): Promise<string> {
    const snapshotPayload: Omit<NoteEditorSnapshot, 'id' | 'createdBy'> & { createdBy?: string } = {
      noteId: input.noteId,
      title: input.title,
      content: input.content,
      createdAt: serverTimestamp(),
      preview: serializeSnapshotPreview(input.content),
      source: input.source,
    };

    if (input.createdBy) {
      snapshotPayload.createdBy = input.createdBy;
    }

    const ref = await addDoc(snapshotsCollection(input.scope, input.noteId), sanitizeDocumentForFirestore(snapshotPayload));
    return ref.id;
  }

  async listEditorSnapshots(scope: ProjectScope, noteId: string): Promise<NoteEditorSnapshot[]> {
    const snapshotsQuery = query(snapshotsCollection(scope, noteId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(snapshotsQuery);
    return editorSnapshotsFromSnapshot(snapshot as FirestoreCollectionSnapshot);
  }

  async loadEditorSnapshot(
    scope: ProjectScope,
    noteId: string,
    snapshotId: string,
  ): Promise<NoteEditorSnapshot | null> {
    const snapshot = await getDoc(snapshotDoc(scope, noteId, snapshotId)) as FirestoreDocumentSnapshot;
    if (!snapshot.exists?.()) {
      return null;
    }
    return editorSnapshotFromDoc(snapshot);
  }
}
